import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AbstractWidgetComponent, ConfigurationService, HttpService, ValidationMessage } from '@sdbn/appclient';
import { ContextChange } from '@sdbn/appclient';
import { TranslationService } from '@sdbn/common';
import { ResponseMessage } from '@sdbn/appclient';
import { TerminalContextConstants } from 'src/tmc-common/model/terminal-context-constants';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';
import { Result } from '@zxing/library';

@Component({
  selector: 'app-registration-with-code-qrcode-combined',
  templateUrl: './registration-with-code-qrcode-combined.html',
  styleUrls: ['./registration-with-code-qrcode-combined.scss']
})
export class RegistrationWithCodeQRCodeCombinedComponent extends AbstractWidgetComponent implements OnInit, AfterViewInit {

  @ViewChild('focusComponent', { static: true }) focusComponent: ElementRef;
  registrationForm: FormGroup;
  responseMessages = new Array<ResponseMessage>();

  validationMessage: ValidationMessage;

  checkCodeActive = false;
  registrationCodeDefaultLength = 10;
  registrationCodeMinLength = this.registrationCodeDefaultLength;
  registrationCodeMaxLength = this.registrationCodeDefaultLength;

  newRegistrationEnabled = false;
  qrCodeScanEnabled = false;
  scannedCode: string = null;
  codeScanFinished = false;
  private terminalHardwareConnectorUrl: string;
  private maxRetries = 5;
  private retryCount = 0;

  scannerType: string = null;

  webCamScannerEnabled = false;

  deviceCurrent: MediaDeviceInfo;
  deviceSelected: string;

  errorMessageDictionaryKey: string;
  codeScanned = false;

  @ViewChild('scanner')
  scanner: ZXingScannerComponent;

  constructor(
    private configurationService: ConfigurationService,
    private httpService: HttpService,
    private formBuilder: FormBuilder,
    public translationService: TranslationService) {
    super();
    this.registrationForm = this.formBuilder.group({
      code: ['', Validators.required]
    });
  }

  ngOnInit(): void {

    this.formdata.code.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(result => {
        if (this.formdata.code.value) {
          const registrationCode = this.formdata.code.value.replace(/\s+/g, '');
          if (registrationCode.length >= this.registrationCodeMinLength && registrationCode.length <= this.registrationCodeMaxLength) {
            console.log('Check entered registrationCode: ', registrationCode);
            this.dashboardContext.setContextValue(TerminalContextConstants.registrationCode, this.formdata.code.value);
          }
        }
      });
  }

  ngAfterViewInit() {
    if (this.focusComponent) {
      // we need to use nativeElement.focus, "autofocus" does not work
      this.focusComponent.nativeElement.focus();
    }
  }

  onContextChange(contextChange: ContextChange) {
    if (contextChange.key === 'qrCodeScannerType' && contextChange.value === 'webcam') {
      this.scannerType = 'webcam';
      this.webCamScannerEnabled = true;
    }
    if (contextChange.key === 'newRegistrationEnabled') {
      this.newRegistrationEnabled = contextChange.value === true;
    }
    if (contextChange.key === 'qrCodeScanEnabled') {
      this.qrCodeScanEnabled = contextChange.value === true;
    }
    if (contextChange.key === 'registrationCodeMinLength') {
      this.registrationCodeMinLength = contextChange.value != null ? this.registrationCodeDefaultLength : contextChange.value;
    }
    if (contextChange.key === 'registrationCodeMaxLength') {
      this.registrationCodeMaxLength = contextChange.value != null ? this.registrationCodeDefaultLength : contextChange.value;
    }
    if (contextChange.key === TerminalContextConstants.isLoading) {
      this.checkCodeActive = contextChange.value;
    }
    if (contextChange.key === TerminalContextConstants.responseMessages) {
      this.responseMessages = contextChange.value;
      if (this.qrCodeScanEnabled) {
        this.initializeAndStartScanner();
      }
    }
    if (contextChange.key === 'validationMessage') {
      this.validationMessage = contextChange.value;
    }
  }

  onSetElementAndContext() {
    const qrCodeScanEnabled = this.dashboardContext.getContextValue('qrCodeScanEnabled');
    this.qrCodeScanEnabled = qrCodeScanEnabled === true;
    if (this.qrCodeScanEnabled) {
      this.initializeAndStartScanner();
    }

    const qrCodeScannerType = this.dashboardContext.getContextValue('qrCodeScannerType');
    if (qrCodeScannerType === 'webcam') {
      this.scannerType = 'webcam';
      this.webCamScannerEnabled = true;
    }

    const newRegistrationEnabled = this.dashboardContext.getContextValue('newRegistrationEnabled');
    if (newRegistrationEnabled != null) {
      this.newRegistrationEnabled = newRegistrationEnabled === true;
    }
    const registrationCodeMinLength = this.dashboardContext.getContextValue('registrationCodeMinLength');
    if (registrationCodeMinLength != null) {
      this.registrationCodeMinLength = registrationCodeMinLength;
    }
    const registrationCodeMaxLength = this.dashboardContext.getContextValue('registrationCodeMaxLength');
    if (registrationCodeMaxLength != null) {
      this.registrationCodeMaxLength = registrationCodeMaxLength;
    }
  }

  ngOnDestroy(): void {
    try {
      if (this.scanner != null) {
        this.scanner.enable = false;
        this.scanner.device = null;
        this.scanner.ngOnDestroy();
      }
    } catch(e) {
      console.log('destroy failed', e);
    }
    this.webCamScannerEnabled = false;

    try {
      this.stopScanner();
    } catch(e) {
      console.log('destroy failed', e);
    }
    
  }

  public camerasFoundHandler(devices: MediaDeviceInfo[]) {

    console.log('camerasFoundHandler', devices);
    if (devices != null && devices.length > 0) {
        this.deviceCurrent = devices[0];
        this.webCamScannerEnabled = true;
    }

  }

  public camerasNotFound($event: any) {
    console.log('camerasNotFound', $event);
    this.errorMessageDictionaryKey = 'tmc-yard.widgets.registration-terminal.visitor-scan-registration-qrcode.error.no-camera-found';
  }

  onCameraDeviceChanged($event: MediaDeviceInfo) {
    console.log('onCameraDeviceChanged', $event);
   }
   onCameraPermission($event: boolean) {
    console.log('onCameraPermission', $event);
   }

  public scanSuccessHandler($event: string) {
    if(!this.codeScanned || this.dashboardContext.getContextValue('barcodeScanned') !== $event){
      this.codeScanned = true;
      this.dashboardContext.setContextValue('barcodeScanned',  $event);
    }

  }

  scanCompleteHandler($event: Result) {
    //console.log("scanCompleteHandler:", $event);
    //this.scanner.scanStop();
    //this.scannerEnabled = false;
  }

  scanErrorHandler($event: Error) {
    console.error('Failed to scan', $event);
  }

  // convenience getter for easy access to form fields
  get formdata() { return this.registrationForm.controls; }

  private initializeAndStartScanner() {
    this.codeScanFinished = false;
    const hardwareConnectorProperty = this.configurationService.getDeviceParam('terminalhardwareconnector.url');
    if (hardwareConnectorProperty == null) {
      console.error('Misconfiguration detected. No TerminalHardwareConnector URL configured');
    } else {
      this.terminalHardwareConnectorUrl = hardwareConnectorProperty.value;
      this.startScanner();
      this.pollForScannedCode();
    }
  }

  private startScanner() {
    this.httpService.post(this.terminalHardwareConnectorUrl + '/io/barcode/start-scanning', {}).subscribe(response => {
      this.pollForScannedCode();
    },
      error => {
        console.error('Failed to call terminal hardware connector. Reason:', error);
      });
  }

  private stopScanner() {
    console.log('execute stop scanner');
    this.httpService.post(this.terminalHardwareConnectorUrl + '/io/barcode/stop-scanning', {}).subscribe(response => {
      console.log('scanning stopped');
    },
      error => {
        console.error('Failed to call terminal hardware connector. Reason:', error);
      });
  }

  private pollForScannedCode() {
    this.retryCount = this.retryCount + 1;
    this.httpService.post(this.terminalHardwareConnectorUrl + '/io/barcode/get-scanned-data', {}).subscribe(response => {
      console.log('Received barcode scan result:', response);
      if (response != null) {
        this.scannedCode = response['value'];
        this.dashboardContext.setContextValue('barcodeScanned', this.scannedCode);
        this.codeScanFinished = true;
        this.stopScanner();
      } else if (this.scannedCode == null && this.retryCount <= this.maxRetries) { // retry
        this.pollForScannedCode();
      }
    },
      error => {
        console.error('Failed to call terminal hardware connector. Reason:', error);
        if (this.retryCount <= this.maxRetries) { // retry
          this.pollForScannedCode();
        }
      });
  }


}
